<template>
    <div class="panel">
        <div v-if="!loaded && !error" class="panel-heading green-heading">
            <content-loader class="rating-loading-indicator" height="71" primary-color="#F2F7FD"
                            secondary-color="#E0EDFB">
                <rect y="13" x="0" width="100%" height="40"/>
            </content-loader>
        </div>
        <div v-if="loaded && !error" class="panel-heading green-heading">
            <star-rating size="2.5rem" :disabled="true" name="rate" class="animated fadein"
                         :value="averageRating">
            </star-rating>
            <span class="panel-title animated fadeIn"><strong>{{
                    averageRating.toFixed(1)
                }} ({{ solutionRatingList.length }})</strong></span>
        </div>
        <div class="panel-body solution-ratings-body">
            <div class="row">
                <div class="col-xs-12">
                    <div v-if="!loaded && !error" class="loading-indicator-wrapper">
                        <template v-if="ratingsCount > 1">
                            <solution-rating-loading-indicator v-for="index in ratingsCount - 1" :key="index"
                                                               :height="60"></solution-rating-loading-indicator>
                        </template>
                        <template v-if="ratingsCount <= 1">
                            <solution-rating-loading-indicator :height="60"></solution-rating-loading-indicator>
                        </template>
                    </div>
                    <div v-if="error" class="error-message">
                        <img class="icon"
                             src="https://res.cloudinary.com/dspi5zyaa/image/upload/v1643639005/eh-platform/icons/smashicons_website_elements_website-error-28-icon-cartoony.svg">
                        <span>Es ist ein <strong>Fehler</strong> beim Laden der Bewertungen
                          aufgetreten :(</span>
                    </div>
                    <div v-if="loaded && !error && solutionRatingList.length === 0">
                        <div>Noch <strong>keine</strong> Bewertung vorhanden.</div>
                    </div>
                    <div v-if="loaded && !error && solutionRatingList.length > 0">
                        <div class="rating animated fadeIn"
                             v-for="rating in solutionRatingList" :class="{'selected': !selectedRatingCommentId && rating.ratingId === selectedSolutionRatingId}"
                             :id="rating.ratingId"
                        >
                            <div class="rating-user" @click="ratingUserClickHandler($event, rating.publicUserId)">
                                <div class="profile-image"
                                     :style="{backgroundImage: 'url(' + rating.userImage + ')'}">
                                </div>
                                <div class="profileName">
                                    <strong>{{ rating.userName }}</strong>
                                    <div class="date">
                                        {{ getNiceTime(rating.timeCreated) }}
                                        <div v-if="rating.solutionId" href="" class="user-solution-link"
                                           :title="'Lösung von ' + rating.userName + ' anzeigen'"
                                           @click="showSolution($event, rating)">
                                            <i class="dev-arrow-right"></i> diese Lösung ansehen
                                            <span>
                                                ( <i class="dev-star"/> {{ rating.averageRating.toFixed(1) }} |
                                                <i class="dev-chat"/> {{ rating.quantityOfComments }} )
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="comment-container">
                                <star-rating class="star-rating" size="1.5rem" :disabled="true" name="rate"
                                             :value="rating.rate"/>
                                <div v-if="/\S/.test(rating.comment)" class="user-rating"
                                     :style="{'margin-bottom: 2rem !important;': !rating.comments || rating.comments.length === 0}">
                                    „{{ rating.comment }}”
                                </div>
                                <solution-rating-comments
                                    @newCommentAdded="updateCommentCount"
                                    :rating="rating"
                                    :selected-comment-id="getSelectedRatingCommentId(rating.ratingId)"
                                    :get-user-name="getUserName"
                                    :get-nice-time="getNiceTime"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {eventBus, services} from "../../main";
import StarRating from "../StarRating";
import {ContentLoader} from "vue-content-loader";
import SolutionRatingLoadingIndicator from "./SolutionRatingLoadingIndicator";
import moment from "moment";
import SolutionRatingComments from "./SolutionRatingComments";

export default {
    name: "SolutionRatingsComponent",
    components: {
        StarRating, ContentLoader, SolutionRatingLoadingIndicator, SolutionRatingComments,
    },
    props: {
        solutionId: {type: String, required: true},
    },
    data() {
        return {
            ratingsRequest: services.rankTheCodeService.getSolutionRatings(this.solutionId),
            userProfileRequest: {loaded: false, error: null},
            publicUserProfilesRequest: {loaded: false, error: null},
            publicUserIdMap: new Map(),
            averageRating: 0,
            ratingsCount: 0,
            selectedSolutionRatingId: null,
            selectedRatingCommentId: null,
        };
    },
    created() {
      this.userProfileRequest = services.userService.getUserProfile();
      this.selectedSolutionRatingId = this.$route.query.solutionRatingId || null;
      this.selectedRatingCommentId = this.$route.query.ratingCommentId || null;
      let query = Object.assign({}, this.$route.query);
      delete query.solutionRatingId;
      delete query.ratingCommentId;
      // dirty hack to remove query params from url, so we catch the error linking to the same site
      this.$router.replace({query}).catch(()=>{});


    },
    updated() {
      if (this.selectedSolutionRatingId) {
        const element = document.getElementById(this.selectedSolutionRatingId);
        if (element) {
          const solutionRating = this.solutionRatingList.find(rating => rating.ratingId === this.selectedSolutionRatingId);
          this.$nextTick(() => {
            element.scrollIntoView({
              block: solutionRating && solutionRating.comments.length === 0 ? "center" : "nearest",
              inline: "nearest"});
          });
        }
      }
    },
    computed: {
        loaded: function () {
            return this.ratingsRequest.loaded && this.publicUserProfilesRequest.loaded && this.userProfileRequest.loaded;
        },
        error: function () {
            return this.ratingsRequest.error || this.publicUserProfilesRequest.error || this.userProfileRequest.error;
        },
        solutionRatingList: function () {
            let solutionRatingList = [];
            if (!this.ratingsRequest.apiData.ratings || !this.publicUserProfilesRequest.loaded || !this.userProfileRequest.loaded) {
                return solutionRatingList;
            }
            this.createPublicUserIdMap();
            for (let rating of this.ratingsRequest.apiData.ratings) {
                rating.userName = this.getUserName(rating.publicUserId);
                rating.userImage = this.getUserImage(rating.publicUserId);
                solutionRatingList.push(rating);
            }
            return solutionRatingList;
        },
    },
    methods: {
        ratingUserClickHandler(event, clickedPublicUserId) {
          let publicUserUrl = this.$router.resolve({
              name: 'public-user-profile',
              params: {userId: clickedPublicUserId}
          }).href;
          window.open(publicUserUrl, clickedPublicUserId);
        },
        showSolution: function (event, rating) {
          const taskId = rating.taskId;
          const solutionId = rating.solutionId;
          eventBus.$emit('show-other-solution', {
            'taskId': taskId,
            'solutionId': solutionId
          });
          event.stopPropagation();
        },
        getSelectedRatingCommentId: function (solutionRatingId) {
          if (solutionRatingId === this.selectedSolutionRatingId) {
            return this.selectedRatingCommentId;
          }
          return null;
        },
        getNiceTime(timestamp) {
            moment.locale("de");
            let dateString = moment(timestamp).format("DD.MM.YY");
            if (moment(timestamp).isSame(moment(), "day")) {
                dateString = moment(timestamp).fromNow();
            } else {
                dateString = 'am ' + dateString;
            }
            return dateString;
        },
        getPublicUserIds() {
            let publicUserIds = [];
            this.ratingsRequest.apiData.ratings.forEach(rating => {
              publicUserIds.push(rating.publicUserId);
              rating.comments.forEach(comment => publicUserIds.push(comment.publicUserId));
            });
            return publicUserIds;
        },
        createPublicUserIdMap() {
            this.publicUserProfilesRequest.apiData.forEach(publicUserData => this.publicUserIdMap.set(publicUserData.publicUserId, {
                displayName: publicUserData.displayName,
                profilePicture: publicUserData.profilePicture
            }));
        },
        getUserName(publicUserId) {
            if (!this.publicUserIdMap.has(publicUserId)) {
                return "Anonymer EntwicklerHeld";
            }
            return this.publicUserIdMap.get(publicUserId)["displayName"];
        },
        getUserImage(publicUserId) {
            if (!this.publicUserIdMap.has(publicUserId)) {
                return "https://s3.amazonaws.com/s3-cuescience/anonym-user.png";
            }
            return this.publicUserIdMap.get(publicUserId)["profilePicture"];
        },
        reloadRatings: function () {
            this.ratingsCount = this.ratingsRequest.apiData && this.ratingsRequest.apiData.ratings ? this.ratingsRequest.apiData.ratings.length : 0;
            this.ratingsCount++;
            this.ratingsRequest = {loaded: false, error: null};
            this.ratingsRequest = services.rankTheCodeService.getSolutionRatings(this.solutionId);
        },
        calculateAverageRating(ratings) {
            if (ratings.ratings.length === 0) {
                return 0;
            }
            let sum = 0;
            for (let rating of ratings.ratings) {
                sum += rating.rate;
            }
            return sum / ratings.ratings.length;
        },
        updateCommentCount: function (amount) {
            this.$emit("newCommentAdded", amount);
        },
    },
    watch: {
        "ratingsRequest.loaded": function () {
            if (this.ratingsRequest.loaded && !this.ratingsRequest.error) {
                let publicUserIds = this.getPublicUserIds();
                this.publicUserProfilesRequest = services.userService.getDataForPublicUserIds(publicUserIds);
                this.averageRating = this.calculateAverageRating(this.ratingsRequest.apiData);
                this.ratingsCount = this.ratingsRequest.apiData.ratings.length;
            }
        },
        "solutionId": function () {
            this.reloadRatings();
        },
        "userProfileRequest.loaded": function () {
          if (!this.userProfileRequest.loaded || this.userProfileRequest.error) {
            return;
          }

          this.publicUserIdMap.set(this.userProfileRequest.user_profile.uuid, {
            displayName: this.userProfileRequest.user_profile.display_name,
            profilePicture: this.userProfileRequest.user_profile.profile_picture
          });
        },
    }
}
</script>

<style scoped>
.panel-heading {
    display: flex;
    justify-content: center;
    align-items: center;
}

.rating-user {
    display: flex;
    align-items: center;
}

.green-heading .panel-title {
    margin-left: 1rem;
    font-size: 1.8rem;
    font-weight: bold;
    color: #0D3359;
}

.rating {
    margin-bottom: 1rem;
    border-radius: 10px;
    padding: 5px 5px;
}

.rating.selected {
  background-color: #fff8ef;
}

.solution-ratings-body {
    max-height: 35vh;
    overflow-y: auto;
}

.rating-user:hover {
    cursor: pointer;
}

.rating-user i {
    margin-right: 0.5rem;
}

.rating-user .profile-image {
    width: 40px;
    height: 40px;
    border-radius: 25px;
    float: left;
    margin-right: 1rem;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.comment-container {
    margin-left: 0.2rem;
}

.date {
    font-size: smaller;
    color: grey;
    display: flex;
    justify-items: center;
}

@media (max-width: 576px) {
    .date {
        flex-direction: column;
    }
}

.rating-user strong {
    white-space: -moz-pre-wrap; /* Firefox */
    white-space: -o-pre-wrap; /* Opera */
    white-space: pre-wrap; /* Chrome */
    word-wrap: break-word; /* IE */
}

.user-rating {
    margin-bottom: 0.5rem;
}

::v-deep .rating .star-rating__star {
    padding-left: 0;
}

.rating-loading-indicator {
    margin: 0 10rem;
}

.user-solution-link {
    font-size: smaller;
    color: #333;
    border-radius: 7px;
    padding: 3px 4px;
    margin-left: 3px;
}

.user-solution-link:hover {
    background-color: lightgray;
}

.error-message {
    padding: 1.5rem;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.icon {
    height: 3rem;
    margin-right: 2rem;
}
</style>
